"use client";

import { useMemo } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";
import classNames from "classnames";
import Image from "next/image";

import { Container } from "@jti/core-components/components/Container";
import type { HeroV2Content, HeroV2Props } from "@jti/ui/components/HeroV2";
import { HeroV2 } from "@jti/ui/components/HeroV2";

import { AuthoringWarningMessage } from "../AuthoringWarningMessage";
import { HERO_VALIDATION_MESSAGE } from "../HeroBannerContainer/HeroBannerContainer";
import { ResponsiveBackgroundImage } from "../HeroBannerContainer/ResponsiveBackgroundImage";
import { HeroDataSchema } from "../HeroBannerContainer/schema";

import styles from "../HeroBannerContainer/HeroBannerContainer.module.scss";

const DEFAULT_LOGO_HEIGHT = 63;
const DEFAULT_LOGO_WIDTH = 100;

type HeroBlockData = Record<string, unknown> & {
  logoAlt: string;
  logoHeight?: number;
  logoWidth?: number;
  removeVideoRoundedCorners?: boolean;
};

function isValidHeroBlock(block: BuilderBlock): block is BuilderBlock & {
  component: { name: string; options: { data: HeroBlockData } };
} {
  return block.component?.name === "Hero" && typeof block.component?.options?.data === "object";
}

interface HeroContainerProps {
  isContainerWidth: boolean;
  isViewportHeight: boolean;
  mobileMediaAtTop: boolean;
  autoplayEnabled?: boolean;
  autoplayDelay?: string;
  breadcrumbsSpacing?: boolean;
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
  attributes: Record<string, string>;
}

export function HeroContainer({
  isContainerWidth,
  isViewportHeight,
  autoplayEnabled = false,
  autoplayDelay = "5",
  breadcrumbsSpacing = false,
  builderBlock,
  builderContext,
  builderComponents,
  attributes,
}: HeroContainerProps) {
  const childBlocks = useMemo(() => builderBlock?.children ?? [], [builderBlock?.children]);

  const convertedData = useMemo(() => {
    return childBlocks
      .filter(Boolean)
      .filter(isValidHeroBlock)
      .map((block, index): ArrayElement<HeroV2Props["data"]> | null => {
        try {
          const heroData = block.component.options.data;
          heroData.removeVideoRoundedCorners = true;
          const validatedHeroData = HeroDataSchema.parse(heroData);

          return {
            ...validatedHeroData,
            content: {
              ...validatedHeroData.content,
              isH1TagDisabled: index > 0,
            },
            renderBackgroundImage: (props) => <ResponsiveBackgroundImage {...props} index={index} />,
            withBreadcrumbs: breadcrumbsSpacing,
            renderImage: ({ src }: { src: string }) => (
              <Image
                src={src}
                alt={heroData.logoAlt}
                height={heroData.logoHeight || DEFAULT_LOGO_HEIGHT}
                width={heroData.logoWidth || DEFAULT_LOGO_WIDTH}
              />
            ),
          };
        } catch (error) {
          console.error("Hero data validation failed", error);
          return null;
        }
      })
      .filter((data): data is HeroV2Content => data !== null);
  }, [childBlocks, breadcrumbsSpacing]);

  const totalHeroChildren = childBlocks.filter(Boolean).filter(isValidHeroBlock).length;
  const hasValidationErrors = convertedData.length !== totalHeroChildren;
  const isSuccessfullyValidated = !hasValidationErrors && convertedData.length > 0;

  if (isEditing()) {
    return (
      <AuthoringWarningMessage isSuccessfullyValidated={isSuccessfullyValidated} message={HERO_VALIDATION_MESSAGE}>
        <Blocks
          blocks={childBlocks}
          parent={builderBlock?.id}
          path="children"
          className={styles.editingWrapper}
          context={builderContext}
          registeredComponents={builderComponents}
        />
      </AuthoringWarningMessage>
    );
  }

  const Wrapper = isContainerWidth ? Container : "div";

  return (
    <Wrapper
      {...attributes}
      className={classNames({
        [styles.fullWidthContainer as string]: !isContainerWidth,
        [styles.fullHeightContainer as string]: !isContainerWidth && isViewportHeight,
      })}
    >
      <HeroV2
        data={convertedData}
        isContainerWidth={isContainerWidth}
        isViewportHeight={isViewportHeight}
        withLoader
        autoplayEnabled={autoplayEnabled}
        autoplayDelayInSeconds={Number(autoplayDelay)}
      />
    </Wrapper>
  );
}
